import type { ReactNode } from "react";

import { percent } from "../lib/format";

export function Card({
  title,
  actions,
  children,
  flush,
}: {
  title?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card__header">
          <h2>{title}</h2>
          {actions}
        </header>
      )}
      <div className={flush ? "card__body card__body--flush" : "card__body"}>{children}</div>
    </section>
  );
}

export function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: ReactNode;
  hint?: ReactNode;
  tone?: "ok" | "warn" | "danger" | "accent";
}) {
  return (
    <div className={`stat${tone ? ` stat--${tone}` : ""}`}>
      <div className="stat__label">{label}</div>
      <div className="stat__value">{value}</div>
      {hint ? <div className="stat__hint">{hint}</div> : null}
    </div>
  );
}

const BADGE_TONES: Record<string, string> = {
  verified: "verified",
  proposed: "proposed",
  claimed: "proposed",
  rejected: "rejected",
  superseded: "neutral",
  disputed: "disputed",
  required: "required",
  recommended: "recommended",
  optional: "optional",
  open: "open",
  resolved: "verified",
  answered: "accent",
  waived: "neutral",
  dismissed: "neutral",
  pending: "open",
  parsed: "verified",
  failed: "rejected",
  unsupported: "neutral",
  in_progress: "accent",
  paused: "disputed",
  completed: "verified",
  complete: "verified",
  not_started: "neutral",
  production: "verified",
  test: "disputed",
  // Assistant health (A8). "unavailable" reads as a refusal, not a neutral shrug — it is the
  // status somebody has to act on.
  ok: "verified",
  degraded: "disputed",
  unavailable: "rejected",
  unknown: "neutral",
  suggested: "proposed",
  accepted: "verified",
  changed: "accent",
  clarification_requested: "open",
  awaiting_confirmation: "open",
  confirmed: "verified",
  declined: "rejected",
  refused: "rejected",
};

export function StatusBadge({ status, label }: { status: string | null | undefined; label?: string }) {
  const key = (status ?? "unknown").toLowerCase();
  return <span className={`badge badge--${BADGE_TONES[key] ?? "neutral"}`}>{label ?? key.replace(/_/g, " ")}</span>;
}

export function Progress({ value }: { value: number }) {
  const clamped = Math.max(0, Math.min(100, value));
  return (
    <div className="row" style={{ gap: 8, flexWrap: "nowrap" }}>
      <div className="progress" aria-label="completion">
        <div className="progress__fill" style={{ width: `${clamped}%` }} />
      </div>
      <span className="small nowrap" style={{ minWidth: 42, textAlign: "right" }}>
        {percent(value)}
      </span>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
      {action}
    </div>
  );
}

export function Loading({ label = "Loading…" }: { label?: string }) {
  return <div className="empty muted">{label}</div>;
}

export function ErrorBanner({ error }: { error: string | undefined }) {
  if (!error) return null;
  return <div className="banner banner--error">{error}</div>;
}

export function Modal({
  title,
  onClose,
  children,
  footer,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="modal__backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <div className="modal" onClick={(event) => event.stopPropagation()}>
        <header className="modal__header">
          <h2>{title}</h2>
          <button className="btn--ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="modal__body">{children}</div>
        {footer ? <footer className="modal__footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <div className="page-header">
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="row">{actions}</div> : null}
    </div>
  );
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      {children}
      {hint ? <div className="field__hint">{hint}</div> : null}
    </div>
  );
}

export function Tabs<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (next: T) => void;
  options: Array<{ value: T; label: string; count?: number }>;
}) {
  return (
    <div className="tabs">
      {options.map((option) => (
        <button
          key={option.value}
          className={`tab${option.value === value ? " is-active" : ""}`}
          onClick={() => onChange(option.value)}
        >
          {option.label}
          {option.count !== undefined ? ` (${option.count})` : ""}
        </button>
      ))}
    </div>
  );
}

export function Triple({
  subject,
  predicate,
  object,
}: {
  subject: string;
  predicate: string;
  object: string | null;
}) {
  return (
    <div className="triple">
      <span className="triple__subject">{subject}</span>
      <span className="triple__predicate">{predicate}</span>
      <span>{object ?? "—"}</span>
    </div>
  );
}
